// Derives small "curated" activity events from the live DOM/history stream:
// click (masked label), navigation, and rage-click. These are NOT part of the
// rrweb /chunks stream -- they go to the channel (postMessage or /signal) so
// the moderator sees a lightweight, human-readable activity feed in near
// real time without needing to decode rrweb events.

import {
  ACTIVITY_MAX_BYTES,
  CURATED_LABEL_MAX_LEN,
  NAV_URL_MAX_LEN,
  RAGE_CLICK_THRESHOLD,
  RAGE_CLICK_WINDOW_MS,
} from './config';
import { isMaskedOrBlocked, type SelectorConfig } from './masking';

export type CuratedActivityType = 'click' | 'rage-click' | 'navigation';

export interface CuratedActivity {
  type: CuratedActivityType;
  data: Record<string, unknown>;
  /** Browser Date.now() at the moment this activity was actually observed
   * (the click/navigation itself) -- NOT when it's eventually sent over
   * /signal, which may be later if a send is queued behind a retry. Lets the
   * backend correlate curated activity with the true wall-clock moment.
   * Optional so a directly-constructed CuratedActivity (e.g. in tests) still
   * type-checks; every real emit site in this file always stamps it. */
  occurred_at?: number;
}

/** UTF-8 byte length without depending on TextEncoder being present in the
 * runtime (keeps this correct for multibyte URLs in any embedding context). */
function utf8ByteLength(str: string): number {
  let bytes = 0;
  for (let i = 0; i < str.length; i += 1) {
    const code = str.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff) {
      bytes += 4; // surrogate pair -> one 4-byte code point
      i += 1;
    } else bytes += 3;
  }
  return bytes;
}

/** Long, potentially-unbounded string fields we truncate first when an
 * activity payload is over the /signal 8KB cap (navigation carries these). */
const TRUNCATABLE_FIELDS = ['url', 'href'] as const;

/**
 * Enforce the ACTIVITY_MAX_BYTES cap on a curated activity before it's emitted
 * into the channel. Returns a payload guaranteed to serialize within the cap,
 * or `null` if it can't be brought under even after truncating known long
 * fields (in which case the caller drops the event). This is the single
 * chokepoint both transports (postMessage + /signal) go through.
 */
export function fitActivityToCap(activity: CuratedActivity): CuratedActivity | null {
  if (utf8ByteLength(JSON.stringify(activity)) <= ACTIVITY_MAX_BYTES) return activity;

  const data: Record<string, unknown> = { ...activity.data };
  for (const field of TRUNCATABLE_FIELDS) {
    const value = data[field];
    if (typeof value === 'string' && value.length > NAV_URL_MAX_LEN) {
      data[field] = value.slice(0, NAV_URL_MAX_LEN);
    }
  }

  const bounded: CuratedActivity = { type: activity.type, data, occurred_at: activity.occurred_at };
  if (utf8ByteLength(JSON.stringify(bounded)) <= ACTIVITY_MAX_BYTES) return bounded;

  // eslint-disable-next-line no-console
  console.warn(
    `[UserIntuitionRecorder] curated activity "${activity.type}" exceeds ${ACTIVITY_MAX_BYTES} bytes ` +
      'even after truncation -- dropping it.',
  );
  return null;
}

export interface CuratedDeps {
  selectors: SelectorConfig;
  onActivity: (activity: CuratedActivity) => void;
  doc?: Document;
  win?: Window;
}

/** Priority order for a human-readable click label. Never falls through to
 * inner text of a masked/blocked element -- see isMaskedOrBlocked() callers. */
function extractLabel(el: Element): string | undefined {
  const htmlEl = el as HTMLElement;
  const raw = (
    el.getAttribute('aria-label') ||
    el.getAttribute('alt') ||
    el.getAttribute('title') ||
    htmlEl.innerText ||
    el.textContent ||
    ''
  )
    .trim()
    .replace(/\s+/g, ' ');
  if (!raw) return undefined;
  return raw.length > CURATED_LABEL_MAX_LEN ? `${raw.slice(0, CURATED_LABEL_MAX_LEN)}` : raw;
}

function describeTarget(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  return `${tag}${id}`;
}

/** Starts click/navigation/rage-click tracking; returns a teardown function. */
export function startCuratedTracking(deps: CuratedDeps): () => void {
  const doc = deps.doc ?? document;
  const win = deps.win ?? window;
  const rageClickTimestamps = new WeakMap<Element, number[]>();

  function handleClick(evt: MouseEvent): void {
    const target = evt.target as Element | null;
    if (!target || typeof target.closest !== 'function') return;

    const masked = isMaskedOrBlocked(target, deps.selectors);
    const label = masked ? undefined : extractLabel(target);
    const now = Date.now();

    deps.onActivity({
      type: 'click',
      data: { target: describeTarget(target), label },
      occurred_at: now,
    });

    const recent = (rageClickTimestamps.get(target) ?? []).filter((t) => now - t < RAGE_CLICK_WINDOW_MS);
    recent.push(now);
    rageClickTimestamps.set(target, recent);
    if (recent.length >= RAGE_CLICK_THRESHOLD) {
      deps.onActivity({
        type: 'rage-click',
        data: { target: describeTarget(target), count: recent.length },
        occurred_at: now,
      });
      rageClickTimestamps.set(target, []); // avoid re-firing on every subsequent click
    }
  }

  function emitNavigation(navType: string): void {
    deps.onActivity({ type: 'navigation', data: { navType, url: win.location.href }, occurred_at: Date.now() });
  }

  doc.addEventListener('click', handleClick, true);

  // Keep the exact original function references (not .bind() copies) so
  // teardown restores history.pushState/replaceState to precisely what they
  // were before -- .apply() below preserves the correct `this` at call time
  // without needing to wrap/replace the stored reference itself.
  const originalPushState = win.history.pushState;
  const originalReplaceState = win.history.replaceState;

  win.history.pushState = function patchedPushState(...args: Parameters<History['pushState']>) {
    originalPushState.apply(win.history, args);
    emitNavigation('pushState');
  };
  win.history.replaceState = function patchedReplaceState(...args: Parameters<History['replaceState']>) {
    originalReplaceState.apply(win.history, args);
    emitNavigation('replaceState');
  };

  const popstateHandler = () => emitNavigation('popstate');
  win.addEventListener('popstate', popstateHandler);

  emitNavigation('load');

  return function stopCuratedTracking(): void {
    doc.removeEventListener('click', handleClick, true);
    win.removeEventListener('popstate', popstateHandler);
    win.history.pushState = originalPushState;
    win.history.replaceState = originalReplaceState;
  };
}
